<script>
import PrincipalHeader from "@/views/pages/dashboard/principal/components/Principal-header.vue";
import PrincipalSidebar from "@/views/pages/dashboard/principal/components/Principal-sidebar.vue";
import BaseLoading from "@/components/CustomComponent/shared/Loading.vue";
import { useSharedStore } from "@/stores/shared-store";
import { useUserStore } from "@/stores/user/user-store";
import { useAuthStore } from "@/stores/auth-store";
import { useAccessClassRequestStore } from "@/stores/access/access-class-request-store";
import { useCourseFetchStore } from "@/stores/course/course-fetch-store.js";

export default {
  components: {
    PrincipalHeader,
    PrincipalSidebar,
    BaseLoading,
  },
  data() {
    return {
      currentPage: 1,
      itemsPerPage: 10,
      pageWindowSize: 6,
      searchKeyword: "",
    };
  },
  computed: {
    sharedStore() {
      return useSharedStore();
    },
    userStore() {
      return useUserStore();
    },
    authStore() {
      return useAuthStore();
    },
    classAccessStore() {
      return useAccessClassRequestStore();
    },
    courseFetchStore() {
      return useCourseFetchStore();
    },
    sortedStudents() {
      const students = this.userStore.ActiveStudents?.slice() || [];
      const keyword = this.searchKeyword.trim().toLowerCase();
      const filtered = keyword
        ? students.filter((student) =>
            this.authStore
              .userName(student?.userID)
              ?.toLowerCase()
              .includes(keyword)
          )
        : students;
      return filtered.sort((a, b) => b.point - a.point);
    },
    totalPages() {
      return Math.max(
        1,
        Math.ceil(this.sortedStudents.length / this.itemsPerPage)
      );
    },
    paginatedStudents() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.sortedStudents.slice(start, end);
    },
    visiblePages() {
      const total = this.totalPages;
      const windowSize = this.pageWindowSize;
      if (total <= windowSize) {
        return Array.from({ length: total }, (_, i) => i + 1);
      }
      const chunkStart =
        Math.floor((this.currentPage - 1) / windowSize) * windowSize + 1;
      const chunkEnd = Math.min(chunkStart + windowSize - 1, total);
      return Array.from(
        { length: chunkEnd - chunkStart + 1 },
        (_, i) => chunkStart + i
      );
    },
    pendingClassRequests() {
      return this.classAccessStore.classRequestList.filter(
        (item) => item.status === "Pending"
      ).length;
    },
  },
  watch: {
    sortedStudents() {
      if (this.currentPage > this.totalPages) {
        this.currentPage = this.totalPages;
      }
    },
  },
  methods: {
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
      }
    },
    onSearch() {
      this.currentPage = 1;
    },
  },
  async mounted() {
    await this.userStore.fetchStudents();
    await this.authStore.authUser();
    await this.classAccessStore.fetchAccessRequests();
    await this.courseFetchStore.fetchCourseList();
  },
};
</script>
<template>
  <layouts-header></layouts-header>
  <!-- Breadcrumb -->
  <div class="breadcrumb-bar text-center">
    <div class="container">
      <div class="row">
        <div class="col-md-12 col-12">
          <h2 class="breadcrumb-title mb-2">Dashboard</h2>
          <nav aria-label="breadcrumb">
            <ol class="breadcrumb justify-content-center mb-0">
              <li class="breadcrumb-item">
                <router-link to="/home">Home</router-link>
              </li>
              <li class="breadcrumb-item active" aria-current="page">
                Dashboard
              </li>
            </ol>
          </nav>
        </div>
      </div>
    </div>
  </div>
  <!-- /Breadcrumb -->
  <div class="content">
    <div class="container">
      <PrincipalHeader></PrincipalHeader>
      <div class="row">
        <PrincipalSidebar></PrincipalSidebar>
        <div class="col-lg-9">
          <div class="row">
            <div class="col-md-12">
              <div class="row">
                <div class="col-md-6">
                  <div class="card">
                    <div class="card-body">
                      <div class="d-flex align-items-center">
                        <span
                          class="icon-box bg-secondary-transparent me-2 me-xxl-3 flex-shrink-0"
                        >
                          <img src="@/assets/img/icon/user-tick.svg" alt="" />
                        </span>
                        <div>
                          <span class="d-block">Class Access Reqeust</span>
                          <h4 class="fs-24 mt-1">{{ pendingClassRequests }}</h4>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="card">
                    <div class="card-body">
                      <div class="d-flex align-items-center">
                        <span
                          class="icon-box bg-success-transparent me-2 me-xxl-3 flex-shrink-0"
                        >
                          <img src="@/assets/img/icon/bookmark.svg" alt="" />
                        </span>
                        <div>
                          <span class="d-block">Course Approve Request</span>
                          <h4 class="fs-24 mt-1">
                            {{ courseFetchStore.unapprovedCourses?.length }}
                          </h4>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div
                class="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3"
              >
                <h5 class="mb-0 fw-bold">Student Point</h5>
                <div class="input-icon" style="min-width: 220px">
                  <span class="input-icon-addon">
                    <i class="isax isax-search-normal-14"></i>
                  </span>
                  <input
                    type="text"
                    class="form-control form-control-md"
                    placeholder="Search student name"
                    v-model="searchKeyword"
                    @input="onSearch"
                  />
                </div>
              </div>
              <div class="table-responsive custom-table">
                <table class="table">
                  <thead class="thead-light">
                    <tr>
                      <th>Student</th>
                      <th>Point</th>
                      <th>Last Course</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="sortedStudents.length === 0">
                      <td colspan="3" class="text-center">No data</td>
                    </tr>
                    <tr
                      v-for="(item, index) in paginatedStudents"
                      :key="item?.userID || index"
                    >
                      <td>
                        <div class="course-title d-flex align-items-center">
                          <img
                            class="avatar avatar-xl flex-shrink-0 me-2"
                            :src="`${
                              authStore.authUrl
                            }/storage/photo/${authStore.userPhoto(
                              item?.userID
                            )}`"
                            alt="Img"
                          />
                          <div>
                            <p class="fw-medium">
                              {{ authStore.userName(item?.userID) }}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td>{{ item?.point }}</td>
                      <td>{{ item.last_completed_course?.courseName }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div
                class="row align-items-center mt-4"
                v-if="sortedStudents.length > 0"
              >
                <div class="col-md-2">
                  <p class="fs-14 fw-500 text-center text-md-start">
                    Page {{ currentPage }} of {{ totalPages }}
                  </p>
                </div>
                <div class="col-md-10">
                  <ul
                    class="pagination lms-page justify-content-center justify-content-md-end mt-2 mt-md-0"
                  >
                    <li
                      class="page-item prev"
                      :class="{ disabled: currentPage === 1 }"
                    >
                      <a
                        class="page-link"
                        href="javascript:void(0)"
                        tabindex="-1"
                        @click="goToPage(currentPage - 1)"
                        ><i class="fas fa-angle-left"></i
                      ></a>
                    </li>
                    <li
                      class="page-item"
                      :class="{ active: currentPage === page }"
                      v-for="page in visiblePages"
                      :key="page"
                    >
                      <a
                        class="page-link"
                        href="javascript:void(0)"
                        @click="goToPage(page)"
                        >{{ page }}</a
                      >
                    </li>
                    <li
                      class="page-item next"
                      :class="{ disabled: currentPage === totalPages }"
                    >
                      <a
                        class="page-link"
                        href="javascript:void(0)"
                        @click="goToPage(currentPage + 1)"
                        ><i class="fas fa-angle-right"></i
                      ></a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <BaseLoading v-show="sharedStore.loading"></BaseLoading>
</template>
